import {
    MultipleRadio,
    Utils,
    MultiTagSelectV2,
    Hooks,
    CompanyIndustry,
    JobFunction,
    CheckBox,
    useHog,
    SingleCheckBox,
    Tooltip,
    Paragraph,
    colors,
    Images,
    Button
} from '@microfrontends/react-components'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { debounce, isKeyWordFromExternal, setCookie } from '../../../../helpers/common'
import SearchResultContext from '../../context'
import filterHelper, { fireGlobalSearchEvent, TSelectData } from '../../helpers/filterHelper'
import { useTranslation } from '../../hooks'
import { IconFilter, IconFilterFilled, IconSortDesc } from '../../icons'
import { TRootState } from '../../searchResultsRootReducers'
import { EPageTab } from '../../types/EPageTag'
import { ISearchFilterParams } from '../../types/ISearchFilterParams'
import { ELanguage } from '../../utils/language'
import translations from './../../translations.json'
import { FilterWrapper, StyledFilterBar, MoreTitle, SelectedBenefit, Clear } from './styled'
import { initialDataProvider } from '@microfrontends/app-shell-v2'
import MoreFilterButton from '../MoreFilterButton'
import SearchSorting, {
    EMostRelevantPositionType,
    ESortingHighlightTooltip,
    ETooltipType,
    HIGHTLIGHT_SORTING_TOOLTIP_NAME
} from '../../../SearchSorting'
import { getCookie, isMobile, isPhone, isSmallPhone } from '../../utils/utilities'
import { TooltipDes } from '../../../SearchSorting/styles'
import {
    MANAGEMENT_JOB_BY_DATE,
    MANAGEMENT_JOB_BY_SALARY,
    MANAGEMENT_JOB_DEFAULT,
    SORT_RELEVANCE_INDEX
} from '../../../../env'
import { SORT_DEFAULT_INDEX, SORT_POSTED_LATEST_INDEX, SORT_POSTED_OLDEST_INDEX, SORT_SALARY_INDEX } from './SortDialog'
import { TAuthUser } from '../../../../handlers/auth/interface'

const delayedFireGlobalSearchEvent = debounce(fireGlobalSearchEvent, 300)

const defaultParams = filterHelper.getDefaultFilterableParams()

type TFilterBarProps = {
    onClickFilter?: () => void
    onClickSort?: () => void
    sortingOptions: {
        sortId: string | number
        isManagementJob: boolean
        showSortingHighlightMobile: boolean
    }
    user: TAuthUser
}

const ALL_VALUE = -1
export default function FilterBar({ onClickFilter, onClickSort, sortingOptions, user }: TFilterBarProps) {
    const { langCode } = useContext(SearchResultContext)
    const { tracking } = useHog()

    const [selectData, lastParams] = useSelector<any, [TSelectData, ISearchFilterParams]>((state) => {
        return [state.searchFilter.selectData, state.searchFilter.lastParams]
    })
    const { salary: salaryOptions = [], level = [], typeWorking = [] } = selectData
    const industries = initialDataProvider.getIndustries()
    const jobFunctions = initialDataProvider.getJobFunction()
    const jobCount = initialDataProvider.getJobFunctionCount()
    const {
        benefitIds = [],
        isUrgentJob,
        categoryId,
        salary,
        jobLevelId,
        typeWorkingId,
        jobFunctionId,
        companyIndustries,
        locations
    } = lastParams

    const pageTab = useSelector<TRootState, EPageTab>((state) => state.globalMisc.pageTab)
    const translate = useTranslation(translations)
    const [showMoreFilter, setShowMoreFilter] = useState(false)
    const [moreFilterSaveOptions, setMoreFilterSaveOptions] = useState({
        salary: [-1, -1],
        benefit: [],
        companyIndustries: [],
        jobLevelId: -1,
        typeWorkingId: -1,
        jobFunctionId: [],
        isUrgentJob: false
    }) // save options of more filter when open drop down
    const [windowWidth, setWindowWidth] = useState<number>(768)
    const matcher = Hooks.useMediaQuery('(max-width: 767px)')
    const [isShowSorting, setIsShowSorting] = useState<boolean>(false)
    const [isHighlightTooltip, setIsHighlightTooltip] = useState<boolean>(false)
    const [isFilterBarFixed, setFilterBarFixed] = useState<boolean>(false)
    const sortId =
        lastParams?.indexName && lastParams?.indexName !== 'default' ? lastParams?.indexName : sortingOptions.sortId

    /*
     * Placement for More button
     * */
    const { refs } = Hooks.useFloating({
        placement: 'bottom-start',
        config: {
            isFlip: false,
            isShift: true
        }
    })

    const renderLevel = useMemo(() => {
        const isTopManagementJobPageTab: boolean = pageTab === EPageTab.MANAGEMENT_JOBS
        const managementLevelIds = [7, 3]
        const cpLevel = [...level]
        const levelOptions = isTopManagementJobPageTab
            ? cpLevel.filter((item) => managementLevelIds.includes(item.id))
            : cpLevel
        levelOptions.unshift({
            id: ALL_VALUE,
            name: translate('All levels')
        })
        return levelOptions
    }, [level, pageTab, translate])

    // Salary is an exception because it uses ID as array
    const normalizedSalaryOptions = useMemo(() => {
        const result = salaryOptions.map((salaryOption) => {
            return {
                id: salaryOption.id.join(',') as unknown as number,
                name: salaryOption.name
            }
        })
        result.unshift({
            id: ALL_VALUE,
            name: translate('All salary ranges')
        })
        return result
    }, [salaryOptions, translate])

    const convertSalaryValue = (salary) => {
        const salaryIdAsString = salary.join(',')
        const salaryValue = (salaryIdAsString === '-1,-1' ? -1 : salaryIdAsString) as unknown as number
        return salaryValue
    }

    const onWindowResize = () => {
        const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 768
        setWindowWidth(windowWidth)
    }

    useEffect(() => {
        const handleClickOnScreen = (e: MouseEvent) => {
            const isClickOutSideDropdown = isClickOutside(e, refs.popup)
            if (showMoreFilter && isClickOutSideDropdown) {
                setShowMoreFilter(false)
            }
        }

        document.addEventListener('keydown', handleKeydown)
        document.addEventListener('mouseup', handleClickOnScreen)

        return () => {
            document.removeEventListener('keydown', handleKeydown)
            document.removeEventListener('mouseup', handleClickOnScreen)
        }
    }, [showMoreFilter])

    /**
     * VNW-17742 Not reset filter after select skill tag
     *
     * @author Tri, Tran Minh
     * @since 5 sept, 2022
     */
    useEffect(() => {
        const benefitChange = !Utils.compareVariable(benefitIds, moreFilterSaveOptions.benefit)
        const salaryChange = !Utils.compareVariable(salary, moreFilterSaveOptions.salary)
        const jobLevelIdChange = !Utils.compareVariable(jobLevelId, moreFilterSaveOptions.jobLevelId)
        const companyIndustriesChange = !Utils.compareVariable(
            companyIndustries,
            moreFilterSaveOptions.companyIndustries
        )
        const typeWorkingIdChange = !Utils.compareVariable(typeWorkingId, moreFilterSaveOptions.typeWorkingId)
        const jobFunctionIdChange = !Utils.compareVariable(jobFunctionId, moreFilterSaveOptions.jobFunctionId)
        const isUrgentJobChange = isUrgentJob !== moreFilterSaveOptions.isUrgentJob

        let filterSaveOptions = moreFilterSaveOptions
        let isChange = false

        if (benefitChange) {
            filterSaveOptions = { ...filterSaveOptions, benefit: benefitIds }
            isChange = true
        }
        if (salaryChange) {
            filterSaveOptions = { ...filterSaveOptions, salary }
            isChange = true
        }
        if (jobLevelIdChange) {
            filterSaveOptions = { ...filterSaveOptions, jobLevelId }
            isChange = true
        }
        if (companyIndustriesChange) {
            filterSaveOptions = { ...filterSaveOptions, companyIndustries }
            isChange = true
        }
        if (typeWorkingIdChange) {
            filterSaveOptions = { ...filterSaveOptions, typeWorkingId }
            isChange = true
        }
        if (jobFunctionIdChange) {
            filterSaveOptions = { ...filterSaveOptions, jobFunctionId }
            isChange = true
        }

        if (isUrgentJobChange) {
            filterSaveOptions = { ...filterSaveOptions, isUrgentJob }
            isChange = true
        }

        if (isChange) {
            setMoreFilterSaveOptions(filterSaveOptions)
        }
    }, [benefitIds, salary])

    useEffect(() => {
        onWindowResize()
        window.addEventListener('resize', onWindowResize)
        return () => {
            window.removeEventListener('resize', onWindowResize)
        }
    }, [])

    const handleSalaryChange = (response) => {
        const textId = response.value.toString()
        const salaryId =
            response.value === -1 ? [-1, -1] : textId.split(',').map((salaryNumber) => parseInt(salaryNumber))
        setMoreFilterSaveOptions({ ...moreFilterSaveOptions, salary: salaryId })
    }

    const handleBenefitChange = (benefit) => {
        setMoreFilterSaveOptions({ ...moreFilterSaveOptions, benefit })
    }

    const handleClearBenefit = () => {
        setMoreFilterSaveOptions({
            ...moreFilterSaveOptions,
            benefit: []
        })
    }

    const handleMoreFilterApply = () => {
        delayedFireGlobalSearchEvent(
            {
                benefitIds: moreFilterSaveOptions.benefit as number[],
                salary: moreFilterSaveOptions.salary,
                jobLevelId: moreFilterSaveOptions.jobLevelId,
                companyIndustries: moreFilterSaveOptions.companyIndustries,
                typeWorkingId: moreFilterSaveOptions.typeWorkingId,
                jobFunctionId: moreFilterSaveOptions.jobFunctionId,
                isUrgentJob: moreFilterSaveOptions.isUrgentJob,
                page: 0
            },
            'FilterBar:Benefit&Salary'
        )
        tracking({
            eventName: 'js_search_result_page:filter_search_result',
            properties: {
                jobFunctionId: moreFilterSaveOptions.jobFunctionId,
                companyIndustryId: moreFilterSaveOptions.companyIndustries,
                jobLevelId: moreFilterSaveOptions.jobLevelId,
                workingTypeId: moreFilterSaveOptions.typeWorkingId,
                salaryRange: moreFilterSaveOptions.salary,
                isUrgent: moreFilterSaveOptions.isUrgentJob,
                keyword: lastParams.query,
                locationId: lastParams.locationId,
                districtId: lastParams.districtId
            }
        })
        setShowMoreFilter(false)
    }

    const handleKeydown = (e) => {
        if (typeof document !== 'undefined') {
            // TODO: need to re-check type of event (keyboardEvent)
            if (showMoreFilter && e.key === 'Tab') {
                const lastTag: any = document.querySelector('.tags-container')?.lastChild
                if (lastTag && lastTag === document.activeElement) {
                    setShowMoreFilter(false)
                }
            }
            if (showMoreFilter && e.key === 'Escape') {
                setShowMoreFilter(false)
            }
        }
    }

    const isClickOutside = (event, ref) => {
        if (ref.current) {
            if (ref.current.contains(event.target)) return false
        }
        return true
    }

    const handleClickClearAll = () => {
        delayedFireGlobalSearchEvent(defaultParams, 'FilterBar:ClearAll')
    }

    const handleOnClose = () => {
        setMoreFilterSaveOptions({
            salary: salary,
            benefit: benefitIds,
            companyIndustries: companyIndustries,
            jobLevelId: jobLevelId,
            typeWorkingId: typeWorkingId,
            jobFunctionId: jobFunctionId,
            isUrgentJob: isUrgentJob
        })
    }

    const handleFilterChange = (filterOptions, from) => {
        const newFilterOptions = {
            ...lastParams,
            ...filterOptions
        }

        delayedFireGlobalSearchEvent(filterOptions, from)

        tracking({
            eventName: 'js_search_result_page:filter_search_result',
            properties: {
                jobFunctionId: newFilterOptions.jobFunctionId,
                companyIndustryId: newFilterOptions.companyIndustries,
                jobLevelId: newFilterOptions.jobLevelId,
                workingTypeId: newFilterOptions.typeWorkingId,
                salaryRange: newFilterOptions.salary,
                isUrgent: newFilterOptions.isUrgentJob,
                keyword: lastParams.query,
                locationId: lastParams.locationId,
                districtId: lastParams.districtId
            }
        })
    }

    const filterNumber = useMemo<number>(() => {
        let hasFilter = 0

        if (categoryId.length > 0) {
            hasFilter++
        }
        if (jobLevelId > -1) {
            hasFilter++
        }
        if (convertSalaryValue(salary) !== -1) {
            // a trick cause salary is an exceptional case
            hasFilter++
        }
        if (isUrgentJob) {
            hasFilter++
        }
        if (typeWorkingId > -1) {
            hasFilter++
        }
        if (benefitIds.length > 0) {
            hasFilter++
        }
        if (companyIndustries.length > 0) {
            hasFilter++
        }
        if (jobFunctionId.length > 0) {
            hasFilter++
        }
        if (matcher && locations.length > 0) {
            hasFilter++
        }
        return hasFilter
    }, [
        matcher,
        categoryId,
        jobLevelId,
        salary,
        isUrgentJob,
        typeWorkingId,
        benefitIds,
        companyIndustries,
        jobFunctionId,
        windowWidth,
        locations
    ])

    const benefitSelected =
        moreFilterSaveOptions.benefit.length > 1 ? translate('benefits selected') : translate('benefit selected')

    const companyIndustriesValue =
        moreFilterSaveOptions.companyIndustries.length > 0 ? moreFilterSaveOptions.companyIndustries[0] : -1

    const onScroll = () => {
        if (!isMobile()) {
            const heightToScrollOffSorting = 70
            const filterBarEl = document.querySelector('.job-sorting-section')
            if (!filterBarEl) {
                return
            }
            const boundingRect = filterBarEl.getBoundingClientRect()
            if (boundingRect.top <= heightToScrollOffSorting) {
                setIsShowSorting(true)
            } else {
                setIsShowSorting(false)
            }
        }

        if (isMobile()) {
            const headerEl = document.querySelector('[data-cname="Header"]')
            let heightToScrollOffSortingMobile = 65
            if (!isPhone()) {
                heightToScrollOffSortingMobile = 0
            }
            if (!headerEl) {
                return
            }
            const headerRectEl = headerEl.getBoundingClientRect()
            if (headerRectEl.top + headerRectEl.height <= heightToScrollOffSortingMobile) {
                setFilterBarFixed(true)
            } else {
                setFilterBarFixed(false)
            }
        }
    }
    useEffect(() => {
        // Initial cookie
        handleHighlightTooltip()

        // Add scroll event listener
        window.addEventListener('scroll', onScroll)
        window.addEventListener('resize', onScroll)

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
        }
    }, [])
    const handleHighlightTooltip = () => {
        if (isKeyWordFromExternal()) {
            setIsHighlightTooltip(false)
            return
        }

        const sortingTooltip = getCookie(HIGHTLIGHT_SORTING_TOOLTIP_NAME)
        if (sortingTooltip === null || sortingTooltip === (ESortingHighlightTooltip.SHOW as string)) {
            setIsHighlightTooltip(true)
            if (sortingTooltip === null) {
                setCookie(HIGHTLIGHT_SORTING_TOOLTIP_NAME, ESortingHighlightTooltip.SHOW as string, ETooltipType.ALL)
            }
            return
        }

        if (sortingTooltip === 'hidden') {
            setIsHighlightTooltip(false)
            return
        }
    }

    useEffect(() => {
        handleLabelSort()
        handleHighlightTooltip()
    }, [sortId])
    const renderTooltipDes = () => {
        return (
            <TooltipDes>
                <Paragraph name={'body'} size={isSmallPhone() ? 'small' : 'medium'} color={colors.grey[100]}>
                    {translate('Select to prioritize viewing most relevant jobs')}
                </Paragraph>
                <div
                    onClick={() => {
                        setIsHighlightTooltip(false)
                        setCookie(
                            HIGHTLIGHT_SORTING_TOOLTIP_NAME,
                            ESortingHighlightTooltip.HIDDEN as string,
                            ETooltipType.IN_DAY
                        )
                    }}
                >
                    <Images.CloseIcon width={18} height={18} fill={colors.grey['100']} />
                </div>
            </TooltipDes>
        )
    }
    const sortOptions = () => {
        const normalJobsSortOptions = [
            {
                id: SORT_DEFAULT_INDEX,
                name: translate('Last update')
            },
            {
                id: SORT_RELEVANCE_INDEX,
                name: translate('Most relevant'),
                isHighlight: true
            },
            {
                id: SORT_SALARY_INDEX,
                name: translate('Salary (high - low)')
            },
            {
                id: SORT_POSTED_LATEST_INDEX,
                name: translate('Latest')
            },
            {
                id: SORT_POSTED_OLDEST_INDEX,
                name: translate('Oldest')
            }
        ]

        const managementJobsSortOptions = [
            {
                id: MANAGEMENT_JOB_DEFAULT,
                name: translate('All')
            },
            {
                id: MANAGEMENT_JOB_BY_DATE,
                name: translate('Latest')
            },
            {
                id: MANAGEMENT_JOB_BY_SALARY,
                name: translate('Salary (high - low)')
            }
        ]

        return sortingOptions?.isManagementJob ? managementJobsSortOptions : normalJobsSortOptions
    }
    const handleLabelSort = () => {
        let text = translate('Sort')
        const sortItem = sortOptions().filter((item) => item.id === sortId)
        if (sortItem.length > 0) {
            text = sortItem[0].name
        }
        return text
    }

    const isShowTooltip =
        !sortingOptions?.isManagementJob &&
        isHighlightTooltip &&
        sortingOptions.showSortingHighlightMobile &&
        sortId !== SORT_RELEVANCE_INDEX

    useEffect(() => {
        if (!isShowTooltip) {
            return
        }
        tracking({
            eventName: 'js_search_result_page:appear_sort_relevant_tooltip'
        })
    }, [isShowTooltip])

    let styleSortingFixed: React.CSSProperties = { position: 'fixed', top: '64px', width: '100%', zIndex: 10 }
    if (isMobile() && !isPhone()) {
        styleSortingFixed = { position: 'fixed', top: '0', width: '100%', zIndex: 10, margin: '0 -8px' }
    }
    return (
        <StyledFilterBar data-name='StyledFilterBar'>
            <div className='desktop desktop-filter-bar' style={isShowSorting ? { gap: 12 } : null}>
                <div className={'desktopFilter'}>
                    <div className='left-bar'>
                        {/* Category */}
                        <div>
                            <JobFunction
                                hiddenFooter
                                langCode={langCode}
                                isMobile={windowWidth <= 992}
                                isEnableFooter={true}
                                data={{ ...jobFunctions, jobCount: jobCount }}
                                value={jobFunctionId}
                                placeholderColor='#333'
                                onChange={(value) => {
                                    handleFilterChange(
                                        {
                                            jobFunctionId: value,
                                            page: 0
                                        },
                                        'FilterBar:Select:Category'
                                    )
                                }}
                                floatingConfig={{
                                    isFlip: false,
                                    position: 'fixed'
                                }}
                                msBaseUrl={process.env.MICROSERVICE_DOMAIN ?? ''}
                                selectLayout={{ type: 'highlight' }}
                            />
                        </div>
                        {/* Company industry */}
                        {windowWidth > 1024 ? (
                            <div className='action'>
                                <CompanyIndustry
                                    isMobile={false}
                                    options={[
                                        { id: -1, nameEn: 'All Industries', name: 'Tất cả lĩnh vực' },
                                        ...industries
                                    ]}
                                    value={companyIndustries.length > 0 ? companyIndustries[0] : -1}
                                    onChange={(e) =>
                                        handleFilterChange(
                                            { companyIndustries: e[0] === -1 ? [] : e, page: 0 },
                                            'FilterBar:Select:CompanyIndustries'
                                        )
                                    }
                                    selectStyle={{ maxWidth: 200 }}
                                    dropdownStyle={{ width: 348 }}
                                    floatingConfig={{
                                        position: 'fixed'
                                    }}
                                    langCode={langCode}
                                    msBaseUrl={process.env.MICROSERVICE_DOMAIN ?? ''}
                                    isEnableSearchAPI
                                    selectLayout={{ type: 'highlight' }}
                                    notCountedAsValue={[-1]}
                                    placeholder={translate('All industries')}
                                    placeholderColor='#333'
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                        {/* Industry */}
                        {windowWidth > 1366 ? (
                            <div className='action'>
                                <MultipleRadio
                                    name='Level'
                                    dropdown={{ width: '280px' }}
                                    selectStyle={{ maxWidth: 200 }}
                                    options={renderLevel}
                                    value={jobLevelId}
                                    onChange={(response) =>
                                        handleFilterChange(
                                            { jobLevelId: response.value as number, page: 0 },
                                            'FilterBar:Select:JobLevel'
                                        )
                                    }
                                    selectLayout={{ type: 'highlight' }}
                                    notCountedAsValue={[-1]}
                                    placeholder={translate('All levels')}
                                    placeholderColor='#333'
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                        {/* Urgent Hiring */}
                        <FilterWrapper style={{ width: 'auto' }}>
                            <SingleCheckBox
                                id='urgent-hiring'
                                label={translate('Urgent hiring')}
                                checked={isUrgentJob}
                                onChecked={(checked) =>
                                    delayedFireGlobalSearchEvent(
                                        { isUrgentJob: !!checked, page: 0 },
                                        'FilterBar:Urgent'
                                    )
                                }
                                hoverBgOff
                                aria-label={translate('Urgent hiring')}
                                isChecked={isUrgentJob}
                                hasBorder={true}
                            />
                        </FilterWrapper>
                        {filterNumber > 0 ? (
                            <Button
                                type='default'
                                outline='none'
                                onClick={() => handleClickClearAll()}
                                disabled={filterNumber === 0}
                            >
                                {translate('Clear')}
                            </Button>
                        ) : null}
                    </div>
                    <div className='right-bar'>
                        <MoreFilterButton
                            filterNumber={filterNumber}
                            handleClickClearAll={handleClickClearAll}
                            handleMoreFilterApply={handleMoreFilterApply}
                            onClose={handleOnClose}
                            langCode={langCode}
                            customOptions={[
                                <div key='key-job-function' className='dropdown-row'>
                                    <MoreTitle>{translate('Job function')}</MoreTitle>
                                    <JobFunction
                                        langCode={langCode}
                                        isMobile={windowWidth <= 992}
                                        isEnableFooter={true}
                                        data={{ ...jobFunctions, jobCount: jobCount }}
                                        value={moreFilterSaveOptions.jobFunctionId}
                                        placeholderColor='#333'
                                        onChange={(value) => {
                                            setMoreFilterSaveOptions({
                                                ...moreFilterSaveOptions,
                                                jobFunctionId: value
                                            })
                                        }}
                                        dropdownStyleOptions={{
                                            width: '100%'
                                        }}
                                        isFlex
                                        floatingConfig={{
                                            position: 'fixed',
                                            enablePortal: true,
                                            useWindowAsFlippingContainer: true,
                                            isFlip: false,
                                            placement: 'bottom-end'
                                        }}
                                        textOptions={{
                                            placeholder: translate('All functions'),
                                            primaryBtnText: translate('Done')
                                        }}
                                        styleOptions={{
                                            isShowSecondaryBtn: false
                                        }}
                                        msBaseUrl={process.env.MICROSERVICE_DOMAIN ?? ''}
                                        displayAsPopup
                                        selectLayout={{ type: 'highlight' }}
                                    />
                                </div>,
                                <div key='key-company-industry' className='dropdown-row'>
                                    <MoreTitle>{translate('Industry')}</MoreTitle>
                                    <CompanyIndustry
                                        isMobile={false}
                                        options={[
                                            { id: -1, nameEn: 'All Industries', name: 'Tất cả lĩnh vực công ty' },
                                            ...industries
                                        ]}
                                        value={companyIndustriesValue}
                                        onChange={(e) => {
                                            setMoreFilterSaveOptions({
                                                ...moreFilterSaveOptions,
                                                companyIndustries: e[0] === -1 ? [] : e
                                            })
                                        }}
                                        floatingConfig={{
                                            position: 'fixed',
                                            enablePortal: true,
                                            useWindowAsFlippingContainer: true
                                        }}
                                        dropdownStyle={{
                                            zIndex: 1101
                                        }}
                                        langCode={langCode}
                                        msBaseUrl={process.env.MICROSERVICE_DOMAIN ?? ''}
                                        isEnableSearchAPI
                                        selectLayout={{ type: 'highlight' }}
                                        notCountedAsValue={[-1]}
                                        placeholder={translate('All industries')}
                                        placeholderColor='#333'
                                    />
                                </div>,
                                <div key='key-level' className='dropdown-row'>
                                    <MoreTitle>{translate('Levels')}</MoreTitle>
                                    <MultipleRadio
                                        name='Level'
                                        options={renderLevel}
                                        value={moreFilterSaveOptions.jobLevelId}
                                        onChange={(response) =>
                                            setMoreFilterSaveOptions({
                                                ...moreFilterSaveOptions,
                                                jobLevelId: response.value as number
                                            })
                                        }
                                        dropdown={{
                                            floatingConfig: {
                                                position: 'fixed',
                                                enablePortal: true,
                                                useWindowAsFlippingContainer: true
                                            }
                                        }}
                                        dropdownStyle={{
                                            zIndex: 1101
                                        }}
                                        selectLayout={{ type: 'highlight' }}
                                        notCountedAsValue={[-1]}
                                        placeholder={translate('All levels')}
                                        placeholderColor='#333'
                                    />
                                </div>,
                                <div key='key-working-type' className='dropdown-row'>
                                    <MoreTitle>{translate('Working type')}</MoreTitle>
                                    <MultipleRadio
                                        name='WorkingType'
                                        options={[
                                            { id: ALL_VALUE, name: translate('All working type') },
                                            ...typeWorking
                                        ]}
                                        value={moreFilterSaveOptions.typeWorkingId}
                                        onChange={(response) =>
                                            setMoreFilterSaveOptions({
                                                ...moreFilterSaveOptions,
                                                typeWorkingId: response.value as number
                                            })
                                        }
                                        dropdown={{
                                            floatingConfig: {
                                                position: 'fixed',
                                                enablePortal: true,
                                                useWindowAsFlippingContainer: true
                                            }
                                        }}
                                        dropdownStyle={{
                                            zIndex: 1101
                                        }}
                                        selectLayout={{ type: 'highlight' }}
                                        notCountedAsValue={[-1]}
                                        placeholder={translate('All working type')}
                                        placeholderColor='#333'
                                    />
                                </div>,
                                <div key='key-job-salary-range' className='dropdown-row'>
                                    <MoreTitle>{translate('Job salary ranges')}</MoreTitle>
                                    <MultipleRadio
                                        name='Level'
                                        options={normalizedSalaryOptions}
                                        value={convertSalaryValue(moreFilterSaveOptions.salary)}
                                        onChange={(response) => handleSalaryChange(response)}
                                        dropdown={{
                                            floatingConfig: {
                                                position: 'fixed',
                                                enablePortal: true,
                                                useWindowAsFlippingContainer: true
                                            }
                                        }}
                                        dropdownStyle={{
                                            zIndex: 1101
                                        }}
                                        selectLayout={{ type: 'highlight' }}
                                        notCountedAsValue={[-1]}
                                        placeholder={translate('All salary ranges')}
                                        placeholderColor='#333'
                                    />
                                </div>,
                                <div key='key-job-benefits' className='dropdown-row'>
                                    <MoreTitle>
                                        {translate('Job Benefits')}
                                        {moreFilterSaveOptions.benefit.length > 0 ? (
                                            <>
                                                <SelectedBenefit>
                                                    {`${moreFilterSaveOptions.benefit.length} ${benefitSelected}`}
                                                </SelectedBenefit>
                                                <Clear onClick={handleClearBenefit}>{translate('Clear')}</Clear>
                                            </>
                                        ) : (
                                            <></>
                                        )}
                                    </MoreTitle>
                                    <MultiTagSelectV2
                                        onChange={(ids) => handleBenefitChange(ids)}
                                        values={moreFilterSaveOptions.benefit}
                                        numOfColumns={3}
                                        langCode={langCode}
                                        maxSelect={15}
                                    />
                                </div>
                            ]}
                            urgentJobComponent={
                                <FilterWrapper style={{ width: langCode === ELanguage.VI ? 115 : 130 }}>
                                    <CheckBox
                                        label={translate('Urgent hiring')}
                                        state={moreFilterSaveOptions.isUrgentJob ? 'checked' : 'none'}
                                        onCheckChange={(value) => {
                                            setMoreFilterSaveOptions({
                                                ...moreFilterSaveOptions,
                                                isUrgentJob: value === 'checked'
                                            })
                                        }}
                                    />
                                </FilterWrapper>
                            }
                        />
                    </div>
                </div>
                {isShowSorting ? (
                    <SearchSorting
                        sortId={sortId}
                        language={langCode}
                        isManagementJob={sortingOptions.isManagementJob}
                        isHighlightTooltip={isHighlightTooltip}
                        positionRender={EMostRelevantPositionType.FILTER_BAR}
                        setHighlightTooltip={handleHighlightTooltip}
                        user={user}
                    />
                ) : null}
            </div>

            <div className='mobile' style={isFilterBarFixed ? styleSortingFixed : null}>
                <div onClick={onClickFilter}>
                    {filterNumber > 0 ? <FilterWithNumber filterNumber={filterNumber} /> : <IconFilter />}
                    <span className='label'>{translate('Filter')}</span>
                </div>
                <div style={{ position: 'relative' }}>
                    <div onClick={onClickSort} className={'sortMobileContains'}>
                        <IconSortDesc />
                        <span className='label'>{handleLabelSort()}</span>
                    </div>
                    {isShowTooltip ? (
                        <Tooltip
                            isShow={isHighlightTooltip && sortId !== SORT_RELEVANCE_INDEX}
                            description={renderTooltipDes()}
                            placement={'customSort'}
                            customStyle={{
                                marginTop: '20px',
                                padding: '15px',
                                maxWidth: 500,
                                borderRadius: '12px',
                                boxShadow: '0px 4px 40px 0px rgba(0, 0, 0, 0.16);',
                                arrowPosition: '75%',
                                bgColor: colors.primary[40]
                            }}
                        />
                    ) : null}
                </div>
            </div>
        </StyledFilterBar>
    )
}

function FilterWithNumber({ filterNumber }: { filterNumber: number }) {
    return (
        <div className='filter-filled'>
            <IconFilterFilled />
            <span className='filter-number'>{filterNumber}</span>
        </div>
    )
}
